import { useCallback } from 'react'
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core'
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable'
import { useState } from 'react'
import { useKanbanStore } from '@/stores/kanbanStore'
import { KanbanColumn } from './KanbanColumn'
import { KanbanCard } from './KanbanCard'
import type { Opportunity, OpportunityStage } from '@/types'

export function KanbanBoard() {
  const { columns, moveOpportunity } = useKanbanStore()
  const [activeOpportunity, setActiveOpportunity] = useState<Opportunity | null>(null)

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const { active } = event
    const opportunity = columns
      .flatMap((col) => col.opportunities)
      .find((opp) => opp.id === active.id)

    if (opportunity) {
      setActiveOpportunity(opportunity)
    }
  }, [columns])

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event

      setActiveOpportunity(null)

      if (!over) return

      const activeId = active.id as string
      const overId = over.id as string

      // Find source column
      const sourceColumn = columns.find((col) =>
        col.opportunities.some((opp) => opp.id === activeId)
      )
      if (!sourceColumn) return

      // Determine target column
      let targetStage: OpportunityStage

      // Check if dropped on a column
      const targetColumn = columns.find((col) => col.id === overId)
      if (targetColumn) {
        targetStage = targetColumn.id
      } else {
        // Dropped on another card - find its column
        const targetCardColumn = columns.find((col) =>
          col.opportunities.some((opp) => opp.id === overId)
        )
        if (!targetCardColumn) return
        targetStage = targetCardColumn.id
      }

      // Move if different column
      if (sourceColumn.id !== targetStage) {
        moveOpportunity(activeId, sourceColumn.id, targetStage)
      }
    },
    [columns, moveOpportunity]
  )

  // "Ganho" (won) permanece visível no board como coluna final (cards levam a tag GANHO);
  // apenas "Perdido" (lost) fica arquivado fora do pipeline.
  const activeColumns = columns.filter(
    (col) => col.id !== 'lost'
  )

  return (
    <div className="h-full overflow-hidden">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="flex gap-4 overflow-x-auto pb-4 h-full px-1">
          {activeColumns.map((column) => (
            <KanbanColumn key={column.id} column={column} />
          ))}
        </div>

        <DragOverlay>
          {activeOpportunity ? (
            <KanbanCard opportunity={activeOpportunity} isDragging />
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  )
}
